import { Box } from "@itandi/itandi-bb-ui/Box";
import React from "react";
import { ConstructionVendorNavigation } from "~/components/common/constructions/navigation/ConstructionVendorNavigation";
import { ReportImageNavigation } from "~/components/common/reports/navigation/ReportImageNavigation";
import { StatusHandleVendorButtons } from "~/components/common/statuses/StatusHandleVendorButtons";
import { UploadFileNavigation } from "~/components/common/upload_files/UploadFileNavigation";
import { ScheduleInputNavigation as VisitScheduleInputNavigation } from "~/components/common/vendors/visit_schedule/ScheduleInputNavigation";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import { useDealCompletionReportImages } from "~/hooks/vendors/deals/useDealCompletionReportImages";
import { useDealEstimationHistories } from "~/hooks/vendors/deals/useDealEstimationHistories";
import { useDealEstimations } from "~/hooks/vendors/deals/useDealEstimations";
import { useDealInspectionReportImages } from "~/hooks/vendors/deals/useDealInspectionReportImages";
import { useDeals } from "~/hooks/vendors/useDeals";
import { useSessions } from "~/hooks/vendors/useSessions";
import type { NskResponse } from "~/models/Api";
import {
	checkInspectionImageRequired,
	ESTIMATION_BY,
	matchDealType
} from "~/models/Deal";
import { navigationTitle } from "~/models/DealReportImage";
import type { DealUploadFile } from "~/models/DealUploadFile";
import {
	getOriginDealVendor,
	isOriginVendor
} from "~/models/DealVendorCompany";
import {
	IN_ESTIMATION,
	SEND_ESTIMATE,
	statusMovingValidation
} from "~/models/Status";
import { PATH as COMPLETE_REPORT_PATH } from "~/pages/vendors/deals/[id]/completion_reports";
import { PATH as INSPECTION_REPORT_PATH } from "~/pages/vendors/deals/[id]/inspection_reports";
import { create as uploadMemorandumFile } from "~/utils/api/vendors/deals/DealMemorandumFile";
import {
	destroy as destroyUploadFile,
	getFilePath as getVendorFilePath
} from "~/utils/api/vendors/deals/DealUploadedFile";

export const InEstimation: React.FC = () => {
	const dealId = useIdQueryHandler();
	const { data: deal, mutate: mutateDeal } = useDeals().show(dealId);
	const { data: estimationContext } = useDealEstimations().index(dealId);
	const { data: dealEstimationHistoryContext } =
		useDealEstimationHistories().index(dealId);
	const { data: inspectionImages } =
		useDealInspectionReportImages().index(dealId);
	const { data: completionImages } =
		useDealCompletionReportImages().index(dealId);
	const { data: session } = useSessions().index();
	const forwardStatusDisabled = React.useMemo(() => {
		if (
			deal === null ||
			estimationContext === null ||
			dealEstimationHistoryContext === null ||
			inspectionImages === null ||
			session === null
		)
			return true;
		return statusMovingValidation[IN_ESTIMATION](
			deal,
			estimationContext,
			dealEstimationHistoryContext,
			inspectionImages,
			session.admin,
			false
		);
	}, [
		deal,
		estimationContext,
		dealEstimationHistoryContext,
		inspectionImages,
		session
	]);
	if (
		deal === null ||
		estimationContext === null ||
		inspectionImages === null ||
		completionImages === null ||
		session === null ||
		dealEstimationHistoryContext === null
	) {
		return null;
	}
	const handleMutateDeal = async (): Promise<void> => {
		await mutateDeal?.();
	};
	const handleUploadMemorandum = async (file: File): Promise<NskResponse> =>
		uploadMemorandumFile(file, deal.id);
	const handleDestroyMemorandum = async (
		file: DealUploadFile
	): Promise<NskResponse> => destroyUploadFile(deal.id, file.id);
	const handleGetMemorandumFilePath = (file: DealUploadFile): string =>
		getVendorFilePath(deal.id, file);

	const statusDescription: React.ReactNode =
		deal.estimationBy === ESTIMATION_BY.COMPANY ? (
			<>見積りが登録されるまでお待ち下さい。</>
		) : (
			<>
				{deal.inspectionBy === "vendor" && (
					<>
						立会日程・現地調査日程を入力し、立会を実施してください。
						<br />
						立会後、
						{checkInspectionImageRequired(deal) && "立会報告画像の登録と"}
					</>
				)}
				見積りの登録を完了させることで、ステータスを進めることができます。
			</>
		);

	return (
		<div>
			<Box marginBottom={16}>
				<p>{statusDescription}</p>
			</Box>
			<VisitScheduleInputNavigation
				deal={deal}
				handleMutate={handleMutateDeal}
			/>
			<ReportImageNavigation
				detailPageButtonLabel="画像登録画面"
				detailPagePath={INSPECTION_REPORT_PATH(deal.id)}
				optionalLabel={!checkInspectionImageRequired(deal)}
				reportImages={inspectionImages}
				title={navigationTitle(deal)}
			/>
			{(matchDealType(deal, "repair") || matchDealType(deal, "other")) && (
				<ReportImageNavigation
					detailPageButtonLabel="画像登録画面"
					detailPagePath={COMPLETE_REPORT_PATH(deal.id)}
					optionalLabel
					reportImages={completionImages}
					title="完了報告画像を登録してください"
				/>
			)}
			{deal.estimationBy === ESTIMATION_BY.VENDOR && (
				<ConstructionVendorNavigation title="見積りを入力してください" />
			)}
			{getOriginDealVendor(deal)?.showTenant ? (
				<UploadFileNavigation
					deal={deal}
					files={deal.dealMemorandumFiles}
					fileType="賃借人同意書"
					handleDestroy={handleDestroyMemorandum}
					handleGetFilePath={handleGetMemorandumFilePath}
					handleMutate={handleMutateDeal}
					handleUpload={handleUploadMemorandum}
					isVendor
					title="賃借人同意書を登録してください"
				/>
			) : null}
			{isOriginVendor(deal, session.vendorCompany.id) && (
				<StatusHandleVendorButtons
					forwardDisabled={!forwardStatusDisabled}
					nextStatusId={SEND_ESTIMATE}
				/>
			)}
		</div>
	);
};
